import { Routes, Route, Navigate, useParams } from 'react-router';
import { useAuth } from './hooks/useAuth';
import { AppLayout } from './components/layout/AppLayout';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { MasterDataPage } from './pages/masterdata/MasterDataPage';
import { EmissionReportsPage } from './pages/emissionReports/EmissionReportsPage';
import { EmissionReportDetailPage } from './pages/emissionReports/EmissionReportDetailPage';
import { UsersPage } from './pages/UsersPage';
import { SettingsPage } from './pages/SettingsPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { ReportEditPage } from './pages/reports/ReportEditPage';
import { ForbiddenPage } from './pages/ForbiddenPage';
import { EvaluationsHubPage } from './pages/evaluations/EvaluationsHubPage';
import { MarketEvaluationPage } from './pages/evaluations/MarketEvaluationPage';
import { AccidentEvaluationPage } from './pages/accident-evaluation/AccidentEvaluationPage';
import { TransferOverviewPage } from './pages/transfers/TransferOverviewPage';
import { StyleGuidePage } from './pages/StyleGuidePage';
import { AuthProvider } from './services/AuthContext';
import { hasReadableTransferArea } from './components/layout/navSections';
import { Agentation } from 'agentation';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function ManageRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  const isAdmin = user?.roles?.includes('ROLE_ADMIN') ?? false;

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function TrusteeRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  const isTrustee = user?.roles?.includes('ROLE_TRUSTEE') ?? false;

  if (!isTrustee) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
}

/**
 * D-01/D-02/D-03: eigener Guard fuer die Unfalldaten-Auswertung -- bewusst enger als
 * ManageRoute (nur Rolle) UND kombiniert mit der per-Benutzer-Kategoriefreischaltung, die
 * keiner der beiden bestehenden Guards prueft. Serverseitig ist der Endpoint bereits doppelt
 * gesichert (ROLE_ADMIN + hasReportCategory) -- dieser Guard ist reine Frontend-Komfortebene
 * (siehe 21-PATTERNS.md § Backend ist die harte Zugriffsgrenze).
 */
function AccidentEvaluationRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  const isAdmin = user?.roles?.includes('ROLE_ADMIN') ?? false;
  const hasAccidentCategory = user?.enabledReportCategories?.includes('accident') ?? false;

  if (!isAdmin || !hasAccidentCategory) {
    return <Navigate to="/dashboard" replace />; // ManageRoute-Fallback, nicht /forbidden (TrusteeRoute)
  }

  return <>{children}</>;
}

/**
 * Guard der Gesamtuebersicht "Uebertragungen" (D-21, D-23, T-41-32): nur Treuhaender/Admin mit
 * mindestens einem lesbaren Bereich duerfen die Seite oeffnen -- dieselbe Regel wie der
 * Navigationseintrag (`hasReadableTransferArea`, `navSections.ts`). Reine Frontend-Komfortebene;
 * das Backend verweigert einen nicht lesbaren Bereich in der URL ohnehin mit 403 (41-05), die
 * Seite zeigt dann "Keine Berechtigung" statt einer leeren Liste.
 */
function TransferRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  const isAdmin = user?.roles?.includes('ROLE_ADMIN') ?? false;
  const isTrustee = user?.roles?.includes('ROLE_TRUSTEE') ?? false;
  const canRead = hasReadableTransferArea({
    isAdmin,
    isTrustee,
    enabledReportCategories: user?.enabledReportCategories ?? [],
  });

  if (!canRead) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
}

/**
 * Bindet die Lebensdauer von ReportEditPage an die konkrete Meldung (WR-01).
 *
 * Der Werks-Auswahlknopf im Meldungskopf navigiert auf `/reports/:type/:id` mit EINER anderen id.
 * React Router tauscht dabei nur den Parameter aus und montiert die Seite NICHT neu — der gesamte
 * meldungsbezogene Zustand ueberlebte deshalb den Wechsel: das submitMode-Ref aus useReportForm
 * (jeder folgende Blur validierte sofort mit mode='submit'), `form.errors` samt roter Felder und
 * Reiterpunkten (`clearInputErrorOnChange: false` loescht sie beim Neuseeden bewusst nicht) und
 * der Warnungs-Store mit den gelben Zellen des Vorgaengers.
 *
 * Der `key` erzwingt genau an dieser Stelle einen Neuaufbau. Bewusst so und nicht als
 * Reset-Aufruf in `fetchReport`: der Reset muesste jeden einzelnen Zustandstraeger kennen und bei
 * jedem neuen Store nachgezogen werden — der Neuaufbau erwischt sie alle, auch kuenftige. Die
 * Wiederbeschaffungskosten sind ohnehin bezahlt, weil `fetchReport` bei geaenderter id sowieso
 * neu laedt.
 */
function ReportEditRoute() {
  const { type, id } = useParams<{ type: string; id: string }>();

  return <ReportEditPage key={`${type ?? ''}/${id ?? ''}`} />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <AuthProvider>
      {import.meta.env.DEV && <Agentation />}
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="reports/:type/:id" element={<ReportEditRoute />} />
          <Route path="review" element={<TrusteeRoute><DashboardPage /></TrusteeRoute>} />
          <Route path="evaluations" element={<TrusteeRoute><EvaluationsHubPage /></TrusteeRoute>} />
          <Route path="evaluations/inlandsversand" element={<TrusteeRoute><MarketEvaluationPage key="inlandsversand" evaluationType="inlandsversand" /></TrusteeRoute>} />
          <Route path="evaluations/versandstatistik" element={<TrusteeRoute><MarketEvaluationPage key="versandstatistik" evaluationType="versandstatistik" /></TrusteeRoute>} />
          <Route path="evaluations/gebietsaufnahme" element={<TrusteeRoute><MarketEvaluationPage key="gebietsaufnahme" evaluationType="gebietsaufnahme" /></TrusteeRoute>} />
          <Route path="evaluations/zementverwendung" element={<TrusteeRoute><MarketEvaluationPage key="zementverwendung" evaluationType="zementverwendung" /></TrusteeRoute>} />
          <Route path="evaluations/zementarten" element={<TrusteeRoute><MarketEvaluationPage key="zementarten" evaluationType="zementarten" /></TrusteeRoute>} />
          <Route path="auswertung/unfalldaten" element={<AccidentEvaluationRoute><AccidentEvaluationPage /></AccidentEvaluationRoute>} />
          <Route path="transfers" element={<TransferRoute><TransferOverviewPage /></TransferRoute>} />
          <Route path="forbidden" element={<ForbiddenPage />} />
          <Route path="master-data" element={<ManageRoute><MasterDataPage /></ManageRoute>} />
          {/* D-01: CompaniesPage/PlantsPage gehen in der Stammdatenseite auf -- Redirects statt
              404, damit bestehende Lesezeichen nicht brechen. */}
          <Route path="companies" element={<Navigate to="/master-data" replace />} />
          <Route path="plants" element={<Navigate to="/master-data" replace />} />
          <Route path="users" element={<ManageRoute><UsersPage /></ManageRoute>} />
          {/* D-07: Vorschau und Protokoll sind dieselbe Route -- kein zweiter Pfad fuer das
              Protokoll, der Unterschied entsteht aus dem Stand des Eintrags. */}
          <Route path="emission-reports" element={<ManageRoute><EmissionReportsPage /></ManageRoute>} />
          <Route path="emission-reports/:id" element={<ManageRoute><EmissionReportDetailPage /></ManageRoute>} />
          <Route path="settings" element={<ManageRoute><SettingsPage /></ManageRoute>} />
          {import.meta.env.DEV && <Route path="styleguide" element={<StyleGuidePage />} />}
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
